import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from './keys';
import type { AuthSource, PermissionMatrix, ProvisionedUser, Role, UserAccount, UserLookupRow } from '../types';
import { sourceLabel } from '@/ui/format';

export function useUsers({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => api<{ users: UserAccount[] }>('/api/users'),
    enabled,
  });
}

/** What each role may do: the Permissions tab of Team & Access. */
export function usePermissionMatrix({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.permissionMatrix,
    queryFn: () => api<PermissionMatrix>('/api/access/permissions'),
    enabled,
    // The catalog changes only with a release.
    staleTime: Infinity,
  });
}

export function useCreateUser() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (user: UserAccount) => `${user.display_name} added` },
    mutationFn: (body: { email: string; display_name?: string; roles: Role[]; auth_source: AuthSource; password?: string }) =>
      api<UserAccount>('/api/users', 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

/** What an update may send; `password` only with a switch to Local. */
export type UserChange = Partial<Pick<UserAccount, 'display_name' | 'email' | 'roles' | 'is_active' | 'auth_source'>> & {
  password?: string;
};

export function useUpdateUser() {
  const client = useQueryClient();
  return useMutation({
    meta: {
      success: (user: UserAccount, { change }: { change: UserChange }) => {
        if (change.auth_source !== undefined) return `Source changed to ${sourceLabel(user.auth_source)}`;
        return change.is_active === undefined || Object.keys(change).length > 1
          ? `${user.display_name} saved`
          : `${user.display_name} ${user.is_active ? 'reactivated' : 'deactivated'}`;
      },
    },
    mutationFn: ({
      id,
      change,
    }: {
      id: string;
      change: UserChange;
    }) => api<UserAccount>(`/api/users/${id}`, 'PATCH', change),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useResetPassword() {
  return useMutation({
    meta: { success: 'Password reset' },
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api<void>(`/api/users/${id}/password`, 'POST', { password }),
  });
}

/** The expert picker's search: active people by name or e-mail, at most 10; asks only from 2 characters. */
export function useUserLookup(q: string, { enabled = true }: { enabled?: boolean } = {}) {
  const needle = q.trim();
  return useQuery({
    queryKey: queryKeys.userLookup(needle.toLowerCase()),
    queryFn: () => api<{ users: UserLookupRow[] }>(`/api/users/lookup?q=${encodeURIComponent(needle)}`),
    enabled: enabled && needle.length >= 2,
    staleTime: 30_000,
  });
}

/** An app account for an expert who has none yet (or the one that exists): it links at their first Microsoft sign-in. */
export function useProvisionUser() {
  const client = useQueryClient();
  return useMutation({
    meta: {
      success: (user: ProvisionedUser) => {
        if (user.notice) return user.notice;
        return user.signed_in || !user.provisioned_at
          ? `${user.display_name} already has an account`
          : `${user.display_name} can be listed as an expert`;
      },
    },
    mutationFn: (body: { email: string; display_name: string }) => api<ProvisionedUser>('/api/users/provision', 'POST', body),
    // The users key is a prefix of the lookups', so both refresh.
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.users }),
  });
}
